import React, {useCallback, useContext, useEffect} from 'react';


import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContext} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {CrmType as CrmTypes} from 'src/openapi/admin/models/CrmType';

import {FlatButton} from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Icon, {Sizes as IconSizes, Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {SlideBoxButton} from '@pb/kit-component-slide-box';

import {ConnectionStatus} from '../../components/connection-status';
import {CrmRow} from '../../components/crm-row';
import {useCrmLimits} from '../../useCrmLimits';
import {useEnabledCrmModules} from '../../useEnabledCrmModules';
import {useLinkageStatistic} from '../../useLinkageStatistic';
import {useSyncLinkageStatistic} from '../../useSyncLinkageStatistic';

import {SalesforceSection} from './salesforce-section';
import {useGetSalesforceAuth} from './useGetSalesforceAuth';
import {useSalesforceCredentials} from './useSalesforceCredentials';
import {useSalesforceSettings} from './useSalesforceSettings';
import {isSalesforceTestReady} from './utils/isSalesforceTestReady';

import type {CrmComponentProps} from '../../license-crm-tab';

const SalesforceRow = (props: CrmComponentProps) => {
    const {
        accountId,
        licenseId,
        currentCrm,

        runSaveChanges,
        runDiscardChanges,

        onChangeActiveCrm,
        onChangesSaved,
        onChangesDiscarded,
        onActionDisabled,
        editDisabled,
    } = props;

    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {clearErrors, validationErrors} = useContext(ValidationContext);

    const {isSomeChunkChanged, isCurrentChunkChanged, setIsChunkChanged, onShowModal, proceed} = useConfirmationChunk({
        chunkName: 'salesforce-crm',
        onProceed: () => {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
            onChangesSaved();
        },
    });

    const {
        isLoading: isModuleLoading,
        loadingError: moduleLoadingError,
        enabled,
        saveCrmModuleState,
    } = useEnabledCrmModules({licenseId, crmType: CrmTypes.SFCRM});

    const {
        isLoading: isLimitsLoading,
        loadingError: limitsLoadingError,
        isChanged: isCrmLimitsChanged,

        data: crmLimits,
        setData: setCrmLimits,
        // expose loadData to be able to refetch on license change
        loadData: loadCrmLimits,

        saveChanges: saveCrmLimits,
        discardChanges: discardCrmLimits,
    } = useCrmLimits({licenseId, crmType: CrmTypes.SFCRM, accountId});

    const {
        isLoading: isLinkageStatisticLoading,
        loadingError: linkageStatisticLoadingError,
        data: linkageStatistic,
        loadData: loadLinkageStatistic,
    } = useLinkageStatistic({accountId, licenseId, crmType: CrmTypes.SFCRM});

    const {
        busy: isSyncLinkageStatisticLoading,
        event: syncSalesforceLinkageStatistic,
    } = useSyncLinkageStatistic({accountId, crmType: CrmTypes.SFCRM});

    const {
        isLoading: isCredentialsLoading,
        isConnectionStatusLoading,
        isUnlinkBusy,
        loadingError: credentialsLoadingError,
        isChanged: isSalesforceCredentialsChanged,

        data: salesforceCredentials,
        setData: setSalesforceCredentials,
        connectionStatus,

        saveChanges: saveSalesforceCredentials,
        unlinkConnection,
        testConnection,
        revokeConnection,
        discardChanges: discardSalesforceCredentials,
    } = useSalesforceCredentials({accountId, isCrmEnabled: enabled});

    const {
        isOAuthRedirectUrlLoading,
        oAuthRedirectUrlLoadingError,
        redirectToSalesforceOAuth,
    } = useGetSalesforceAuth({
        accountId,
        sandbox: salesforceCredentials.sandbox,
        onConnectionSuccessful: () => testConnection(salesforceCredentials),
    });

    const {
        isLoading: isSettingsLoading,
        loadingError: settingsLoadingError,
        isChanged: isSalesforceSettingsChanged,

        data: salesforceSettings,
        setData: setSalesforceSettings,

        saveChanges: saveSalesforceSettings,
        discardChanges: discardSalesforceSettings,
        loadData: loadSalesforceSettings,
    } = useSalesforceSettings({accountId});

    const saveChanges = useCallback(async () => {
        const calls = [];

        if (validationErrors.length) {
            setErrorMessage(BASIC_VALIDATION_FAILED);
            onChangesSaved();
            return;
        }

        if (isCrmLimitsChanged) {
            calls.push(await saveCrmLimits());
        }

        if (isSalesforceCredentialsChanged) {
            calls.push(await saveSalesforceCredentials());
        }

        if (isSalesforceSettingsChanged) {
            calls.push(await saveSalesforceSettings());
        }

        const errors = calls.filter(Boolean);

        if (errors.length) {
            onChangesSaved();
            setErrorMessage(errors.join(' '));
        } else {
            proceed();
        }
    }, [
        isCrmLimitsChanged,
        saveCrmLimits,
        isSalesforceCredentialsChanged,
        saveSalesforceCredentials,
        isSalesforceSettingsChanged,
        saveSalesforceSettings,
        validationErrors,
        onChangesSaved,
        proceed,
        setErrorMessage,
    ]);

    const connectByPassword = useCallback(async () => {
        const connectionError = await saveSalesforceCredentials();

        if (connectionError) {
            setErrorMessage(connectionError);
        }
    }, [saveSalesforceCredentials, setErrorMessage]);

    const discardChanges = useCallback(() => {
        discardCrmLimits();
        discardSalesforceCredentials();
        discardSalesforceSettings();

        clearErrors();
        onChangesDiscarded();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [
        onChangesDiscarded,
        setSuccessMessage,
        discardCrmLimits,
        discardSalesforceCredentials,
        discardSalesforceSettings,
        clearErrors,
    ]);

    const changeActiveCrm = useCallback(() => {
        if (isCurrentChunkChanged) {
            onShowModal(() => {
                discardChanges();
                onChangeActiveCrm(currentCrm !== CrmTypes.SFCRM ? CrmTypes.SFCRM : null);
            });
        } else {
            onChangeActiveCrm(currentCrm !== CrmTypes.SFCRM ? CrmTypes.SFCRM : null);
        }
    }, [isCurrentChunkChanged, discardChanges, currentCrm, onChangeActiveCrm, onShowModal]);

    const toggleCrmModules = useCallback<React.MouseEventHandler<HTMLDivElement>>(event => {
        if (enabled && isCurrentChunkChanged) {
            onShowModal(() => {
                discardChanges();
                onChangeActiveCrm(null);
                saveCrmModuleState(false);
            });
        } else if (enabled) {
            onChangeActiveCrm(null);
            saveCrmModuleState(false);
        } else {
            saveCrmModuleState(true);
        }
        event.preventDefault();
    }, [isCurrentChunkChanged, discardChanges, enabled, onChangeActiveCrm, saveCrmModuleState, onShowModal]);

    const syncLinkageStatistic = useCallback(async () => {
        const error = await syncSalesforceLinkageStatistic();

        if (error) {
            setErrorMessage(error);
        } else {
            setSuccessMessage('Reconciliation completed');
            if (loadLinkageStatistic) {
                loadLinkageStatistic();
            }
        }
    }, [syncSalesforceLinkageStatistic, loadLinkageStatistic, setErrorMessage, setSuccessMessage]);

    useEffectWithDeps((prev, current) => {
        if (currentCrm === CrmTypes.SFCRM && prev.currentCrm !== current.currentCrm) {
            if (loadLinkageStatistic) {
                loadLinkageStatistic();
            }

            if (loadSalesforceSettings) {
                loadSalesforceSettings();
            }
        }
    }, {
        currentCrm,
        loadSalesforceSettings,
        loadLinkageStatistic,
    });

    useEffect(() => {
        if (licenseId) {
            if (loadCrmLimits) {
                loadCrmLimits();
            }
            if (loadLinkageStatistic) {
                loadLinkageStatistic();
            }
            if (loadSalesforceSettings) {
                loadSalesforceSettings();
            }
        }
    }, [licenseId, loadCrmLimits, loadLinkageStatistic, loadSalesforceSettings]);

    const isRowLoading = [
        moduleLoadingError,
        limitsLoadingError,
    ].some(Boolean);

    const loadingError = [
        credentialsLoadingError,
        settingsLoadingError,
        oAuthRedirectUrlLoadingError,
    ].find(Boolean) || null;

    const isLoading = [
        isModuleLoading,
        isLimitsLoading,
        isCredentialsLoading,
        isSettingsLoading,
    ].some(Boolean);

    useEffect(() => {
        setIsChunkChanged([
            isCrmLimitsChanged,
            isSalesforceCredentialsChanged,
            isSalesforceSettingsChanged,
        ].some(Boolean));
    }, [
        setIsChunkChanged,
        isCrmLimitsChanged,
        isSalesforceCredentialsChanged,
        isSalesforceSettingsChanged,
    ]);

    useEffect(() => {
        if (runSaveChanges && isCurrentChunkChanged) {
            saveChanges();
        }
    }, [runSaveChanges, saveChanges, isCurrentChunkChanged]);

    useEffect(() => {
        if (runDiscardChanges && isCurrentChunkChanged) {
            discardChanges();
        }
    }, [runDiscardChanges, discardChanges, isCurrentChunkChanged]);

    const isActionDisabled = !isCurrentChunkChanged
        || isLoading
        || isRowLoading
        || !!loadingError
        || isConnectionStatusLoading;

    useEffect(() => {
        onActionDisabled(isActionDisabled);
    }, [isActionDisabled, onActionDisabled]);

    return (
        <CrmRow
            isLoading={isRowLoading}
            currentCrm={currentCrm}
            loadingError={loadingError}
            crmType={CrmTypes.SFCRM}
            crmName="Salesforce"
            editDisabled={editDisabled}
            enabled={enabled}
            onToggleCrmModules={toggleCrmModules}
            crmLimits={crmLimits}
            connectionStatus={(
                <ConnectionStatus {...{
                    enabled,
                    isTestReady: isSalesforceTestReady(salesforceCredentials),
                    connectionStatus,
                    unlinkBusy: isUnlinkBusy,
                    onUnlink: unlinkConnection,
                }}
                />
            )}
            slideBoxButton={(
                <SlideBoxButton onClick={changeActiveCrm}>
                    {() => (
                        <Item className="pdy-xs">
                            {enabled && isSalesforceTestReady(salesforceCredentials) ? (
                                <Group alignItems="center">
                                    <FlatButton
                                        startIcon={<Icon type={Icons.REPEAT}/>}
                                        busy={isConnectionStatusLoading}
                                        onClick={() => testConnection(salesforceCredentials)}>
                                        <b>Test connection</b>
                                    </FlatButton>
                                    <Item className="pr-xs">
                                        <BasicIconButton
                                            iconProps={{
                                                type: currentCrm === CrmTypes.SFCRM ? Icons.ANGLE_UP : Icons.ANGLE_DOWN,
                                                size: IconSizes.Size_12,
                                            }}
                                            disabled={
                                                isSomeChunkChanged(chunk => chunk.includes('crm')) &&
                                                currentCrm !== CrmTypes.SFCRM
                                            }
                                            onClick={changeActiveCrm}
                                        />
                                    </Item>
                                </Group>
                            ) : (
                                <FlatButton
                                    disabled={
                                        isSomeChunkChanged(chunk => chunk.includes('crm')) &&
                                        currentCrm !== CrmTypes.SFCRM
                                    }
                                    startIcon={<Icon type={Icons.PENCIL}/>}
                                    onClick={changeActiveCrm}
                                    endIcon={(
                                        <Icon type={currentCrm === CrmTypes.SFCRM
                                            ? Icons.ANGLE_UP
                                            : Icons.ANGLE_DOWN}
                                        />
                                    )}>
                                    Configure settings
                                </FlatButton>
                            )}
                        </Item>
                    )}
                </SlideBoxButton>
            )}>
            <SalesforceSection
                isSyncLinkageLoading={isSyncLinkageStatisticLoading}
                isLinkageStatisticLoading={isLinkageStatisticLoading}
                linkageStatisticLoadingError={linkageStatisticLoadingError}
                isLoading={isLoading}
                isConnectionStatusLoading={isConnectionStatusLoading}
                isOAuthRedirectUrlLoading={isOAuthRedirectUrlLoading}
                disabled={!enabled}
                readonly={editDisabled}

                limits={crmLimits}
                credentials={salesforceCredentials}
                settings={salesforceSettings}
                linkageStatistic={linkageStatistic}
                orgId={connectionStatus?.status.crmUserInfo?.orgId}

                onChangeLimits={setCrmLimits}
                onSyncLinkageStatistic={syncLinkageStatistic}
                onChangeCredentials={setSalesforceCredentials}
                onChangeSettings={setSalesforceSettings}
                onSetupPasswordLogin={connectByPassword}
                onSetupOAuthLogin={redirectToSalesforceOAuth}
                onRevokeOAuthToken={revokeConnection}
            />
        </CrmRow>
    );
};

export const ValidatedSalesforceRow = (props: CrmComponentProps) => (
    <ValidationContextProvider>
        <SalesforceRow {...props}/>
    </ValidationContextProvider>
);
